/**
 * Memoised shallow selectors over DebateStore — keep React re-renders bounded
 * to the minimum viable slice surface.
 * Invariants: selectors are pure; no derived objects allocated unless inputs changed.
 */
import { shallow } from "zustand/shallow";
import type { DebateStore } from "./debateStore";
import type {
  ConsensusSnapshot,
  Critique,
  Persona,
  Turn,
  Verdict,
} from "../domain/types";

export { shallow };

export const selectCurrentTurn = (s: DebateStore): Turn | null => {
  if (!s.currentTurnId) return null;
  return s.turnsById[s.currentTurnId] ?? null;
};

/**
 * Resolve the persona that currently "owns the room" for visual focus
 * purposes (spotlight, dim others, broadcast indicator). Mirrors the
 * legacy ad-hoc derivation in `RoundtableStage`:
 *
 *   - Unmuted runs: prefer the **audible** speaker (TTS frame is
 *     actively pumping audio) over the *committed* current speaker
 *     over the previous speaker.
 *   - Muted runs: skip the audible signal and fall back to the
 *     transport-layer current/previous speaker.
 *
 * Returns `null` when no one has yet held the floor.
 */
export const selectFocusSpeakerId = (s: DebateStore): string | null => {
  if (!s.muted) {
    return s.audibleSpeakerId ?? s.currentSpeakerId ?? s.previousSpeakerId ?? null;
  }
  return s.currentSpeakerId ?? s.previousSpeakerId ?? null;
};

export const selectSpeakerOf =
  (turnId: string) =>
  (s: DebateStore): Persona | null => {
    const t = s.turnsById[turnId];
    if (!t) return null;
    return s.personas[t.speakerId] ?? null;
  };

export const selectActiveCritique = (s: DebateStore): Critique | null => {
  if (!s.currentTurnId) return null;
  const t = s.turnsById[s.currentTurnId];
  return t?.critique ?? null;
};

export interface ConsensusGauge {
  scalarScore: number;
  round: number;
  pending: boolean;
  deadlock: boolean;
  shouldFinalize: boolean;
}

export const selectConsensusGauge = (s: DebateStore): ConsensusGauge => {
  const snap: ConsensusSnapshot | null = s.consensus;
  return {
    scalarScore: snap?.scalarScore ?? 0,
    round: snap?.round ?? s.roundIndex,
    pending: s.consensusPending,
    deadlock: snap?.deadlock ?? false,
    shouldFinalize: snap?.shouldFinalize ?? false,
  };
};

export const selectVerdict = (s: DebateStore): Verdict | null => s.verdict;

/* ====================================================================== *
 * Verdict-time aggregate metrics — feeds the post-debate "MetricsStrip"
 * banner and the PDF report header. Derived purely from the canonical
 * turn store; never side-effectful.
 * ====================================================================== */

export interface SpeakerStat {
  speakerId: string;
  displayName: string;
  expertise: string | null;
  themeColor: string | null;
  turns: number;
  characters: number;
  /** Share of total transcript characters (0..1). */
  share: number;
}

export interface DebateAggregateMetrics {
  /** Total turns that produced a `spoke` outcome (forfeits excluded). */
  totalTurns: number;
  /** Highest `roundIndex` observed +1 (1-based for human display). */
  totalRounds: number;
  /** Wall-clock duration in seconds, or null if no `endedAt` yet. */
  durationSeconds: number | null;
  /** Sum of `finalText.length` across all spoken turns. */
  totalCharacters: number;
  /** Total research sources collected across every turn. */
  totalSources: number;
  /**
   * Total RAG citations consumed across every turn (post-dedupe by
   * docId#chunkIdx so the same chunk reused by N speakers counts once).
   */
  totalRagCitations: number;
  /** Per-speaker rollup, ordered by descending character contribution. */
  bySpeaker: SpeakerStat[];
}

const turnText = (t: Turn): string => {
  const finalText = t.finalText?.trim();
  if (finalText) return finalText;
  return (t.tokens ?? []).join("").trim();
};

export const selectDebateMetrics = (s: DebateStore): DebateAggregateMetrics => {
  const turns = s.turnOrder.map((id) => s.turnsById[id]).filter(Boolean) as Turn[];
  const spokeTurns = turns.filter((t) => t.outcome === "spoke" && turnText(t));

  const charsBySpeaker = new Map<string, number>();
  const turnsBySpeaker = new Map<string, number>();
  let totalChars = 0;
  let totalSources = 0;
  let lastEndedAt: number | null = null;
  let maxRound = -1;

  // RAG dedupe: a chunk reused by 5 speakers should be ONE citation in
  // the aggregate metric (the bibliography below dedupes the same way).
  const ragSeen = new Set<string>();
  for (const t of turns) {
    if (t.endedAt && (lastEndedAt === null || t.endedAt > lastEndedAt)) {
      lastEndedAt = t.endedAt;
    }
    if (t.roundIndex > maxRound) maxRound = t.roundIndex;
    for (const r of t.research ?? []) {
      totalSources += r.sources.length;
    }
    for (const b of t.ragBatches ?? []) {
      for (const c of b.citations) {
        ragSeen.add(`${c.docId}#${c.chunkIdx}`);
      }
    }
  }
  for (const t of spokeTurns) {
    const len = turnText(t).length;
    totalChars += len;
    charsBySpeaker.set(t.speakerId, (charsBySpeaker.get(t.speakerId) ?? 0) + len);
    turnsBySpeaker.set(t.speakerId, (turnsBySpeaker.get(t.speakerId) ?? 0) + 1);
  }

  const bySpeaker: SpeakerStat[] = Array.from(charsBySpeaker.entries())
    .map(([speakerId, characters]) => {
      const persona = s.personas[speakerId];
      return {
        speakerId,
        displayName: persona?.displayName ?? speakerId.slice(0, 8),
        expertise: persona?.expertise ?? null,
        themeColor: persona?.themeColor ?? null,
        turns: turnsBySpeaker.get(speakerId) ?? 0,
        characters,
        share: totalChars > 0 ? characters / totalChars : 0,
      };
    })
    .sort((a, b) => b.characters - a.characters);

  const durationSeconds =
    s.startedAt && lastEndedAt
      ? Math.max(0, Math.round((lastEndedAt - s.startedAt) / 1000))
      : null;

  return {
    totalTurns: spokeTurns.length,
    totalRounds: maxRound + 1,
    durationSeconds,
    totalCharacters: totalChars,
    totalSources,
    totalRagCitations: ragSeen.size,
    bySpeaker,
  };
};

/**
 * Flattened list of every research source observed during the debate,
 * each tagged with the speaker that fetched it. Powers the verdict
 * "Kaynaklar" tab and the PDF source bibliography.
 */
export interface FlatResearchSource {
  speakerId: string;
  speakerName: string;
  themeColor: string | null;
  query: string;
  source: import("../domain/types").ResearchSource;
}

export const selectAllResearchSources = (s: DebateStore): FlatResearchSource[] => {
  const out: FlatResearchSource[] = [];
  const seen = new Set<string>();
  for (const id of s.turnOrder) {
    const t = s.turnsById[id];
    if (!t || !t.research) continue;
    const persona = s.personas[t.speakerId];
    for (const r of t.research) {
      for (const src of r.sources) {
        const key = `${src.url}::${t.speakerId}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({
          speakerId: t.speakerId,
          speakerName: persona?.displayName ?? t.speakerId.slice(0, 8),
          themeColor: persona?.themeColor ?? null,
          query: r.query,
          source: src,
        });
      }
    }
  }
  return out;
};

/**
 * Flattened list of every RAG citation injected during the debate.
 * Powers the verdict report's "Belge Atıfları" bibliography. We dedupe
 * by (docId, chunkIdx) — the same chunk reused by N speakers shows once
 * — but tag the *first* speaker who consumed it for traceability.
 */
export interface FlatRagCitation {
  speakerId: string;
  speakerName: string;
  themeColor: string | null;
  query: string;
  citation: import("../domain/types").RagCitation;
  /** How many distinct speakers ended up referencing this same chunk. */
  reuseCount: number;
}

export const selectAllRagCitations = (s: DebateStore): FlatRagCitation[] => {
  const byKey = new Map<string, FlatRagCitation>();
  const speakerSeenPerKey = new Map<string, Set<string>>();
  for (const id of s.turnOrder) {
    const t = s.turnsById[id];
    if (!t || !t.ragBatches || t.ragBatches.length === 0) continue;
    const persona = s.personas[t.speakerId];
    for (const batch of t.ragBatches) {
      for (const c of batch.citations) {
        const key = `${c.docId}#${c.chunkIdx}`;
        const speakerSet =
          speakerSeenPerKey.get(key) ?? new Set<string>();
        speakerSet.add(t.speakerId);
        speakerSeenPerKey.set(key, speakerSet);
        if (!byKey.has(key)) {
          byKey.set(key, {
            speakerId: t.speakerId,
            speakerName: persona?.displayName ?? t.speakerId.slice(0, 8),
            themeColor: persona?.themeColor ?? null,
            query: batch.query,
            citation: c,
            reuseCount: 1,
          });
        }
      }
    }
  }
  // Refresh reuseCount from the speaker map (the value at insertion was 1).
  const out: FlatRagCitation[] = [];
  for (const [key, entry] of byKey) {
    const speakers = speakerSeenPerKey.get(key);
    out.push({
      ...entry,
      reuseCount: speakers ? speakers.size : 1,
    });
  }
  // Sort by score descending → most relevant chunks float to the top
  // of the bibliography. Ties broken by filename then chunkIdx for
  // a stable, audit-friendly order.
  out.sort((a, b) => {
    const ds = b.citation.score - a.citation.score;
    if (Math.abs(ds) > 1e-6) return ds;
    const dn = a.citation.filename.localeCompare(b.citation.filename, "tr");
    if (dn !== 0) return dn;
    return a.citation.chunkIdx - b.citation.chunkIdx;
  });
  return out;
};

export const selectSessionLifecycle = (
  s: DebateStore,
): {
  status: DebateStore["status"];
  sessionId: string | null;
  startedAt: number | null;
  error: Error | null;
} => ({
  status: s.status,
  sessionId: s.sessionId,
  startedAt: s.startedAt,
  error: s.error,
});

export const selectRoundtable = (
  s: DebateStore,
): { seatOrder: string[]; personas: Record<string, Persona> } => ({
  seatOrder: s.seatOrder,
  personas: s.personas,
});

export const selectInterrupt = (
  s: DebateStore,
): { active: boolean; pivot: string | null } => ({
  active: s.interruptActive,
  pivot: s.lastModeratorPivot,
});
